import {
  buildSahDeliveryCheckQueueItems,
  hasUnsatisfiedDeliveryCheck,
} from '@statura/utils/sah-readiness-resolver';
import { isSahMeansBasisComplete } from '@statura/utils/sah';
import type { AssessmentSummaryView, SahPanelModel } from '@/components/clients/useSahBudgetModel';
import {
  CREATE_SERVICE_AGREEMENT_ACTION,
  isAgreementReadinessBlockerKey,
  type CreateServiceAgreementAction,
} from '@/components/clients/sah-service-agreement-dialog-helpers';
import {
  buildSahActionQueue,
  buildSahLifecycleSteps,
  isSahLifecycleAssessment,
  type LifecycleStep,
  type SahActionItem,
} from '@/lib/sah/sah-client-journey';
import {
  carePlanComposeHref,
  type ClientCarePlanReturnContext,
} from '@/lib/sah/client-care-plan-navigation';
import {
  sahClientCreateAgreementHref,
  sahClientFinanceHandoffHref,
  sahClientDecisionMakersHref,
  sahClientReadinessHref,
  sahClientHcpOpeningHref,
  sahClientMacIdHref,
  sahClientOnboardingPackHref,
  sahClientServicesTabHref,
} from '@/lib/sah/sah-href-contract';
import {
  resolveSahCarePlanWorkspaceHref,
  sahCarePlanReturnContext,
  sahCarePlanStartHref,
} from '@/lib/sah/sah-care-plan-start';
import { resolveSahDeliveryCheckNavigationRoute } from '@/lib/sah/sah-delivery-check-navigation';
import type { SahCarePlanNavigationOptions } from '@/lib/sah/sah-care-plan-start';
import {
  SAH_BUDGET_PROVISION_QUEUE_DETAIL,
  SAH_BUDGET_PROVISION_QUEUE_TITLE,
} from '@/lib/sah/sah-budget-provision-copy';

export { sahCarePlanStartHref } from '@/lib/sah/sah-care-plan-start';

export const SAH_PACK_OPEN_STATUSES = new Set(['DRAFT', 'IN_PROGRESS', 'READY', 'STALLED']);

export type PackQueueSummary = {
  packId: string | null;
  hasOpenPack: boolean;
  totalTasks: number;
  pendingTasks: number;
};

export type UnifiedSahActionSource =
  | 'lifecycle'
  | 'onboarding_pack'
  | 'readiness_projection'
  | 'budget_workflow'
  | 'overview_clinical'
  | 'finance'
  | 'delivery_handoff'
  | 'care_plan_review'
  | 'restorative';

export type UnifiedSahActionSeverity = 'blocker' | 'review' | 'watch';

export type UnifiedSahAction = {
  id: string;
  title: string;
  detail: string;
  href?: string;
  actionKey?: CreateServiceAgreementAction;
  owner: string;
  source: UnifiedSahActionSource;
  severity: UnifiedSahActionSeverity;
  phaseKey?: string;
};

export type UnifiedSahWorkflowContext = {
  hasMacId: boolean;
  assessmentsReady: boolean;
  carePlansReady: boolean;
  hasCompletedAssessment: boolean;
  hasOpenCarePlan: boolean;
  hasAnyServiceEvidence: boolean;
  hasPostedDeliveryEvidence: boolean;
  waitlistClient: boolean;
};

export type UnifiedSahActionNavigation = {
  /** Prefer client Delivery tab over the book schedules route when visible. */
  servicesTabVisible?: boolean;
  /** Budget-tab finance handoff anchor (statements + claims launchpad). */
  budgetFinanceHandoffHref?: string;
  /** Zone B carve-out already surfaces imminent/overdue review — omit queue duplicate. */
  suppressCarePlanReviewQueueItem?: boolean;
};

export type UnifiedSahActionQueueInput = {
  data: SahPanelModel;
  clientId: string;
  tz: string;
  plannedServicesOverBudget: boolean;
  canManageServiceAgreement: boolean;
  packSummary?: PackQueueSummary | null;
  workflow?: UnifiedSahWorkflowContext;
  navigationOptions?: SahCarePlanNavigationOptions;
  navigation?: UnifiedSahActionNavigation;
};

function clientOverviewMacHref(clientId: string): string {
  return sahClientMacIdHref(clientId);
}

const SEVERITY_RANK: Record<UnifiedSahActionSeverity, number> = {
  blocker: 0,
  review: 1,
  watch: 2,
};

export function sahBudgetFundingHref(clientId: string): string {
  return `/clients/${clientId}?tab=budget&panel=funding#funding`;
}

export function sahBudgetFundingSetupHref(clientId: string): string {
  return `/clients/${clientId}?tab=budget&panel=funding-setup#funding-setup`;
}

function lifecycleOwner(stepKey: string): string {
  if (stepKey === 'budget') return 'Finance/admin';
  if (stepKey === 'care-plan') return 'Coordinator';
  if (stepKey === 'assessments') return 'Clinical lead';
  if (stepKey === 'pack') return 'Coordinator';
  return 'Coordinator';
}

function lifecycleSeverity(stepKey: string): UnifiedSahActionSeverity {
  if (['intake', 'authority', 'care-plan', 'budget', 'delivery', 'pack'].includes(stepKey)) {
    return 'blocker';
  }
  return 'review';
}

export function lifecycleActionTitle(step: Pick<LifecycleStep, 'key' | 'label'>): string {
  const titles: Record<string, string> = {
    intake: 'Complete intake and funding',
    authority: 'Complete evidence and authority',
    assessments: 'Complete assessments',
    'care-plan': 'Complete care plan and services',
    budget: 'Complete individualised budget',
    delivery: 'Complete delivery readiness',
    pack: 'Complete onboarding pack',
  };
  return titles[step.key] ?? `Complete ${step.label.toLowerCase()}`;
}

function fromLifecycleStep(step: LifecycleStep): UnifiedSahAction {
  return {
    id: `lifecycle-${step.key}`,
    title: lifecycleActionTitle(step),
    detail: step.detail,
    href: step.href,
    actionKey: step.actionKey,
    owner: lifecycleOwner(step.key),
    source: 'lifecycle',
    severity: lifecycleSeverity(step.key),
    phaseKey: step.key,
  };
}

const LEGACY_PANEL_ACTION_PHASE: Record<string, string> = {
  'pending-financials': 'delivery',
  'planned-over-budget': 'budget',
  'active-restorative': 'delivery',
  'care-plan-review': 'care-plan',
};

function legacyItemPhaseKey(item: SahActionItem): string | undefined {
  if (item.id.startsWith('delivery-check-')) {
    return deliveryCheckPhaseKey(item.id.slice('delivery-check-'.length));
  }
  if (item.id.startsWith('readiness-')) {
    return deliveryCheckPhaseKey(item.id.slice('readiness-'.length));
  }
  return LEGACY_PANEL_ACTION_PHASE[item.id];
}

function fromLegacyQueueItem(item: SahActionItem): UnifiedSahAction {
  let source: UnifiedSahActionSource = 'readiness_projection';
  if (item.source === 'SAH lifecycle') source = 'lifecycle';
  if (item.source === 'Service delivery finance') source = 'finance';
  if (item.source === 'SAH quarter budget') source = 'budget_workflow';
  if (item.source === 'Care plan review') source = 'care_plan_review';
  if (item.source === 'Restorative care') source = 'restorative';
  const phaseKey = legacyItemPhaseKey(item);
  return {
    id: item.id,
    title: item.title,
    detail: item.detail,
    href: item.href,
    actionKey: item.actionKey,
    owner: item.owner,
    source,
    severity: item.severity,
    ...(phaseKey ? { phaseKey } : {}),
  };
}

function deliveryCheckPhaseKey(code: string): string {
  switch (code) {
    case 'ACTIVE_SUPPORT_AT_HOME_PLAN':
    case 'ACKNOWLEDGED_CARE_PLAN':
    case 'DATE_VALID_SERVICE_INCLUSIONS':
      return 'care-plan';
    case 'SIGNED_SERVICE_AGREEMENT':
    case 'GENERAL_CARE_CONSENT':
      return 'authority';
    case 'CURRENT_QUARTER_BUDGET':
    case 'HCP_OPENING_RECORDED':
      return 'budget';
    case 'SAH_MEANS_BASIS':
      return 'intake';
    default:
      return 'delivery';
  }
}

function currentPlanComposerHref(
  input: UnifiedSahActionQueueInput,
  hash: string,
  returnContext?: ClientCarePlanReturnContext,
): string {
  if (!input.data.currentSupportPlanId) {
    return sahCarePlanStartHref(input.clientId);
  }
  return carePlanComposeHref(input.data.currentSupportPlanId, {
    hash,
    returnContext: returnContext ?? sahCarePlanReturnContext(input.clientId),
  });
}

function deliveryCheckActionTarget(
  input: UnifiedSahActionQueueInput,
  item: ReturnType<typeof buildSahDeliveryCheckQueueItems>[number],
): Pick<UnifiedSahAction, 'href' | 'actionKey'> {
  const { clientId } = input;
  const signatoryBlocked = input.data.readiness.blockers.some(
    (blocker) => blocker.key === 'signatory',
  );

  switch (item.code) {
    case 'ACTIVE_SUPPORT_AT_HOME_PLAN':
      return { href: currentPlanComposerHref(input, 'composer-activate') };
    case 'ACKNOWLEDGED_CARE_PLAN':
      return { href: currentPlanComposerHref(input, 'composer-agree') };
    case 'DATE_VALID_SERVICE_INCLUSIONS':
      return { href: currentPlanComposerHref(input, 'services') };
    case 'SIGNED_SERVICE_AGREEMENT':
      if (signatoryBlocked) return { href: sahClientDecisionMakersHref(clientId) };
      if (!input.canManageServiceAgreement) {
        // Non-managers cannot generate agreements — the raw item.href deep-links
        // ?action=create-agreement, which only fires a role-refusal toast for
        // them (wiring audit P2). Route to the readiness view instead (legacy
        // queue parity, sah-client-journey.ts).
        return { href: sahClientReadinessHref(clientId) };
      }
      if (input.data.currentSupportPlanId) {
        return {
          href: resolveSahDeliveryCheckNavigationRoute({
            code: 'SIGNED_SERVICE_AGREEMENT',
            clientId,
            carePlanId: input.data.currentSupportPlanId,
            planStatus: input.data.carePlans.find(
              (plan) => plan.id === input.data.currentSupportPlanId,
            )?.status,
            isSupportAtHomePlan: true,
            returnContext: sahCarePlanReturnContext(clientId),
          }).href,
        };
      }
      return {
        actionKey: CREATE_SERVICE_AGREEMENT_ACTION,
        href: sahClientCreateAgreementHref(clientId),
      };
    case 'CURRENT_QUARTER_BUDGET':
      return { href: sahBudgetFundingHref(clientId) };
    case 'HCP_OPENING_RECORDED':
      return { href: sahClientHcpOpeningHref(clientId) };
    case 'SAH_MEANS_BASIS':
      return { href: sahBudgetFundingSetupHref(clientId) };
    default:
      return { href: item.href };
  }
}

function pushAction(items: UnifiedSahAction[], seen: Set<string>, item: UnifiedSahAction) {
  if (seen.has(item.id)) return;
  seen.add(item.id);
  items.push(item);
}

function mergeWorkflowActions(
  input: UnifiedSahActionQueueInput,
  items: UnifiedSahAction[],
  seen: Set<string>,
) {
  const workflow = input.workflow;
  if (!workflow) return;

  const { clientId } = input;

  if (workflow.waitlistClient) {
    pushAction(items, seen, {
      id: 'finish-admission-readiness',
      title: 'Finish admission readiness',
      detail:
        'Complete location, funding, address and service-area details before moving this referral into active care.',
      href: clientOverviewMacHref(clientId),
      owner: 'Coordinator',
      source: 'overview_clinical',
      severity: 'blocker',
      phaseKey: 'intake',
    });
  }

  if (!workflow.hasMacId) {
    // NOTE: input.data.isTransitioner and input.data.fundingContext.* must both
    // originate from a single resolveClientFundingContext call (they do, via
    // useSahBudgetModel) so they cannot drift out of agreement here.
    if (input.data.isTransitioner) {
      // HCP→SAH transitioners reconcile against their Home Care Package transition
      // record, not a fresh SAH MAC-ID capture. Direct the coordinator to exactly
      // what must be true for this transitioner's funding — never a silent skip.
      const transitionGaps = [
        input.data.transitionedHcpLevel == null ? 'the transitioned HCP level (1-4)' : null,
        input.data.fundingContext.isNoWorseOff
          ? 'the no-worse-off contribution basis from the Services Australia determination'
          : 'the Services Australia means/contribution basis',
        'any carried-over (unspent) HCP funds',
        'the My Aged Care ID for reconciliation',
      ].filter(Boolean) as string[];
      pushAction(items, seen, {
        id: 'confirm-hcp-transition-funding',
        title: 'Confirm Home Care Package transition funding',
        detail: `This client transitioned from a Home Care Package. Record ${transitionGaps.join(
          ', ',
        )} so their Support at Home budget, agreement and statements reconcile.`,
        href: sahBudgetFundingSetupHref(clientId),
        owner: 'Coordinator',
        source: 'overview_clinical',
        severity: 'review',
        phaseKey: 'intake',
      });
    } else {
      pushAction(items, seen, {
        id: 'capture-my-aged-care',
        title: 'Capture My Aged Care identifiers',
        detail:
          'Record the My Aged Care ID and funding context so classification, budgets and service authorisations reconcile from one record.',
        href: clientOverviewMacHref(clientId),
        owner: 'Coordinator',
        source: 'overview_clinical',
        severity: workflow.waitlistClient ? 'review' : 'blocker',
        phaseKey: 'intake',
      });
    }
  }

  if (workflow.assessmentsReady && !workflow.hasCompletedAssessment) {
    pushAction(items, seen, {
      id: 'complete-initial-assessment',
      title: 'Complete the initial assessment',
      detail:
        'Capture functional, clinical and home-safety evidence before budget, care-plan and service decisions.',
      href: `/clients/${clientId}/assessments`,
      owner: 'Clinical lead',
      source: 'overview_clinical',
      severity: workflow.waitlistClient ? 'review' : 'blocker',
      phaseKey: 'assessments',
    });
  }

  const hasMeans = isSahMeansBasisComplete(
    input.data.means.dbTier,
    input.data.means.assessedIndependence,
    input.data.means.assessedEverydayLiving,
  );
  if (
    !hasMeans &&
    !input.data.heldByHeadProvider &&
    !hasUnsatisfiedDeliveryCheck(input.data.deliveryChecks, 'SAH_MEANS_BASIS')
  ) {
    pushAction(items, seen, {
      id: 'means-basis',
      title: 'Record the contribution basis',
      detail: input.data.fundingContext.isNoWorseOff
        ? 'Record the no-worse-off contribution basis from the Services Australia determination (or the nil/full-pensioner basis if the determination is nil) before issuing agreements, statements or budget advice. A fresh SAH means assessment is not required — grandfathered rates apply.'
        : 'Confirm the client contribution tier before issuing agreements, statements or budget advice.',
      href: sahBudgetFundingSetupHref(clientId),
      owner: 'Finance/admin',
      source: 'budget_workflow',
      severity: 'blocker',
      phaseKey: 'intake',
    });
  }

  if (
    !input.data.currentBudget &&
    !hasUnsatisfiedDeliveryCheck(input.data.deliveryChecks, 'CURRENT_QUARTER_BUDGET')
  ) {
    const budgetProvisionHref = input.data.currentSupportPlanId
      ? resolveSahCarePlanWorkspaceHref(
          input.data,
          input.clientId,
          input.data.currentSupportPlanId,
          input.navigationOptions,
        )
      : sahBudgetFundingHref(clientId);
    pushAction(items, seen, {
      id: 'budget-provision',
      title: SAH_BUDGET_PROVISION_QUEUE_TITLE,
      detail: SAH_BUDGET_PROVISION_QUEUE_DETAIL,
      href: budgetProvisionHref,
      owner: 'Finance/admin',
      source: 'budget_workflow',
      severity: 'blocker',
      phaseKey: 'budget',
    });
  }

  if (workflow.carePlansReady && !workflow.hasOpenCarePlan) {
    pushAction(items, seen, {
      id: 'create-care-plan',
      title: 'Create the care plan',
      detail:
        'Open the start panel and mint a draft support plan. That lands in compose — not the book-wide care-plan list.',
      href: sahCarePlanStartHref(clientId),
      owner: 'Coordinator',
      source: 'lifecycle',
      severity: workflow.hasCompletedAssessment ? 'review' : 'blocker',
      phaseKey: 'care-plan',
    });
  }

  const currentPlan = input.data.currentSupportPlanId
    ? input.data.carePlans.find((plan) => plan.id === input.data.currentSupportPlanId)
    : null;
  const currentPlanStatus = String(currentPlan?.status ?? '').toUpperCase();
  if (
    workflow.carePlansReady &&
    workflow.hasOpenCarePlan &&
    input.data.currentSupportPlanId &&
    ['DRAFT', 'IN_REVIEW', 'PENDING_APPROVAL'].includes(currentPlanStatus)
  ) {
    pushAction(items, seen, {
      id: 'activate-care-plan',
      title: 'Activate the care plan',
      detail:
        'Approve and activate the support plan so funded scheduling, delivery readiness and claims can proceed.',
      href: input.data.currentSupportPlanId
        ? resolveSahCarePlanWorkspaceHref(
            input.data,
            input.clientId,
            input.data.currentSupportPlanId,
            { ...input.navigationOptions, intent: 'delivery' },
          )
        : undefined,
      owner: 'Coordinator',
      source: 'lifecycle',
      severity: 'blocker',
      phaseKey: 'care-plan',
    });
  }

  if (input.data.deliveryReady && workflow.hasOpenCarePlan && !workflow.hasAnyServiceEvidence) {
    const scheduleHref = input.navigation?.servicesTabVisible
      ? sahClientServicesTabHref(clientId)
      : `/home-care/schedules?clientId=${clientId}`;
    pushAction(items, seen, {
      id: 'schedule-first-service',
      title: 'Schedule the first service',
      detail:
        'Move the agreed plan into rostered delivery so visits, evidence, claims and statements can flow.',
      href: scheduleHref,
      owner: 'Coordinator',
      source: 'delivery_handoff',
      severity: 'review',
      phaseKey: 'delivery',
    });
  }

  if (input.data.deliveryReady && workflow.hasOpenCarePlan) {
    const financeHandoffHref =
      input.navigation?.budgetFinanceHandoffHref ?? sahClientFinanceHandoffHref(clientId);
    pushAction(items, seen, {
      id: 'prepare-sah-statement-invoices',
      title: 'Send statement and draft invoices',
      detail:
        'Issue the monthly statement, generate draft participant contribution invoices, and keep contribution evidence tied to delivered services.',
      href: financeHandoffHref,
      owner: 'Finance/admin',
      source: 'delivery_handoff',
      severity: 'review',
      phaseKey: 'delivery',
    });
    pushAction(items, seen, {
      id: 'prepare-sah-claims-reconciliation',
      title: 'Prepare claims and reconciliation',
      detail:
        'Review claim readiness, generate the Services Australia batch, record provider-portal submission, acknowledgement, and remittance reconciliation.',
      href: input.navigation?.budgetFinanceHandoffHref ?? sahClientFinanceHandoffHref(clientId),
      owner: 'Finance/admin',
      source: 'delivery_handoff',
      severity: 'review',
      phaseKey: 'delivery',
    });
  }
}

/** Follow-on instrument statuses that surface a "complete it" queue action. */
const OPEN_FOLLOW_ON_ASSESSMENT_STATUSES = new Set(['IN_PROGRESS', 'SCHEDULED']);

/**
 * Human label for an open follow-on assessment action. Template-code first,
 * assessment-type fallback — same derivation as `assessmentScreenLabel` in
 * client-record-action-queue.ts, replicated here rather than imported because
 * that module already imports from this one (a new import back would add a
 * module cycle).
 */
export function openFollowOnAssessmentLabel(
  assessment: Pick<AssessmentSummaryView, 'assessment_type' | 'template_version'>,
): string {
  const code = String(assessment.template_version ?? '').split(':')[0] ?? '';
  const slug = code
    .replace(/^platform_/, '')
    .replace(/_/g, ' ')
    .trim();
  if (slug) return slug.charAt(0).toUpperCase() + slug.slice(1);
  const type = String(assessment.assessment_type ?? '')
    .replace(/_/g, ' ')
    .trim()
    .toLowerCase();
  if (type) return type.charAt(0).toUpperCase() + type.slice(1);
  return '';
}

export function openFollowOnAssessmentActionTitle(
  assessment: Pick<AssessmentSummaryView, 'assessment_type' | 'template_version'>,
): string {
  const label = openFollowOnAssessmentLabel(assessment);
  if (!label) return 'Complete assessment';
  // Template slugs like "Home safety assessment" already carry the noun.
  return /assessment$/i.test(label) ? `Complete ${label}` : `Complete ${label} assessment`;
}

/**
 * Follow-on instruments (auto-created PCOC SAS runs, cognitive screens, …) are
 * IN_PROGRESS/SCHEDULED assessments OUTSIDE the SAH lifecycle type set, so the
 * lifecycle "Complete assessments" step never counted them and the journey hub
 * lost them entirely (Golden Bloom finding, 2026-07-18). Emit one action per
 * open non-lifecycle assessment, mirroring the generic client-record pattern
 * (`open-assessment-*` items in client-record-action-queue.ts).
 *
 * Severity stays at the default 'review' tier: the assessments row carries no
 * priority column, and the source recommendation's priority lives in
 * assessment_recommendations, which the SahPanelModel assembly does not load —
 * so it is not reachable cheaply here.
 *
 * Deliberately NO phaseKey: dedupeSupersededLifecycleActions drops the coarse
 * `lifecycle-assessments` row when any non-lifecycle item claims the
 * 'assessments' phase, and that row tracks the *initial* intake instrument —
 * an open cognitive screen must not silence it.
 */
function mergeOpenFollowOnAssessmentActions(
  input: UnifiedSahActionQueueInput,
  items: UnifiedSahAction[],
  seen: Set<string>,
) {
  for (const assessment of input.data.assessments) {
    const status = String(assessment.status ?? '').toUpperCase();
    if (!OPEN_FOLLOW_ON_ASSESSMENT_STATUSES.has(status)) continue;
    if (assessment.completed_at) continue;
    if (isSahLifecycleAssessment(assessment)) continue;
    pushAction(items, seen, {
      id: `open-assessment-${assessment.id}`,
      title: openFollowOnAssessmentActionTitle(assessment),
      detail: `This follow-on assessment is ${
        status === 'SCHEDULED' ? 'scheduled' : 'in progress'
      } and is waiting to be completed.`,
      href: `/assessments/${assessment.id}`,
      owner: 'Clinical lead',
      source: 'overview_clinical',
      severity: 'review',
    });
  }
}

function mergePackActions(
  input: UnifiedSahActionQueueInput,
  items: UnifiedSahAction[],
  seen: Set<string>,
) {
  const pack = input.packSummary;
  if (!pack?.hasOpenPack || pack.pendingTasks <= 0) return;
  pushAction(items, seen, {
    id: 'pack-tasks',
    title: 'Complete onboarding pack tasks',
    detail: `${pack.pendingTasks} onboarding pack task${pack.pendingTasks === 1 ? '' : 's'} still open.`,
    href: sahClientOnboardingPackHref(input.clientId),
    owner: 'Coordinator',
    source: 'onboarding_pack',
    severity: 'watch',
    phaseKey: 'pack',
  });
}

function mergeDeliveryCheckBlockers(
  input: UnifiedSahActionQueueInput,
  items: UnifiedSahAction[],
  seen: Set<string>,
) {
  // The head provider holds these for an associated provider's brokered client,
  // the same rule the Readiness register applies.
  const localDeliveryChecks = input.data.heldByHeadProvider
    ? input.data.deliveryChecks.filter(
        (check) => check.code !== 'SIGNED_SERVICE_AGREEMENT' && check.code !== 'SAH_MEANS_BASIS',
      )
    : input.data.deliveryChecks;
  for (const item of buildSahDeliveryCheckQueueItems(localDeliveryChecks, {
    clientId: input.clientId,
    carePlanId: input.data.currentSupportPlanId,
  })) {
    const actionTarget = deliveryCheckActionTarget(input, item);
    pushAction(items, seen, {
      id: item.id,
      title: item.title,
      detail: item.detail,
      ...actionTarget,
      owner: item.owner,
      source: 'readiness_projection',
      severity: item.severity,
      phaseKey: deliveryCheckPhaseKey(item.code),
    });
  }
}

/** Re-export lifecycle queue extras (financials, over-budget, restorative, reviews). */
function mergePanelQueueExtras(
  input: UnifiedSahActionQueueInput,
  items: UnifiedSahAction[],
  seen: Set<string>,
) {
  const steps = buildSahLifecycleSteps(
    input.data,
    input.clientId,
    input.canManageServiceAgreement,
    input.packSummary,
    input.navigationOptions,
  );
  const legacy = buildSahActionQueue(
    input.data,
    steps,
    input.clientId,
    input.tz,
    input.plannedServicesOverBudget,
    input.canManageServiceAgreement,
  );
  for (const item of legacy) {
    if (item.id.startsWith('lifecycle-')) continue;
    if (item.id.startsWith('readiness-')) {
      const mapped = fromLegacyQueueItem(item);
      pushAction(items, seen, mapped);
      continue;
    }
    pushAction(items, seen, fromLegacyQueueItem(item));
  }
}

/** Lifecycle rows superseded when a more specific queue item is already present. */
const LIFECYCLE_SUPERSEDED_WHEN_PRESENT: Record<string, readonly string[]> = {
  'lifecycle-assessments': ['complete-initial-assessment'],
  'lifecycle-pack': ['pack-tasks'],
};

/**
 * Drop coarse lifecycle steps when a phase-specific action already owns the signal
 * (spec §2 one-fact-one-place). Delivery lifecycle is retained alongside G-C checks.
 */
export function dedupeSupersededLifecycleActions(
  items: readonly UnifiedSahAction[],
): UnifiedSahAction[] {
  const ids = new Set(items.map((item) => item.id));
  const nonLifecyclePhaseKeys = new Set(
    items
      .filter((item) => !item.id.startsWith('lifecycle-') && item.phaseKey)
      .map((item) => item.phaseKey as string),
  );
  const phaseLifecycleSuperseded = new Set([
    'assessments',
    'care-plan',
    'authority',
    'intake',
    'budget',
    'pack',
  ]);

  return items.filter((item) => {
    if (!item.id.startsWith('lifecycle-')) return true;

    const explicitSuperseders = LIFECYCLE_SUPERSEDED_WHEN_PRESENT[item.id];
    if (explicitSuperseders?.some((actionId) => ids.has(actionId))) return false;

    const phase = item.phaseKey;
    if (phase && phaseLifecycleSuperseded.has(phase) && nonLifecyclePhaseKeys.has(phase)) {
      return false;
    }

    return true;
  });
}

export function buildUnifiedSahActionQueue(input: UnifiedSahActionQueueInput): UnifiedSahAction[] {
  const items: UnifiedSahAction[] = [];
  const seen = new Set<string>();

  const steps = buildSahLifecycleSteps(
    input.data,
    input.clientId,
    input.canManageServiceAgreement,
    input.packSummary,
    input.navigationOptions,
  );

  for (const step of steps.filter((step) => !step.complete)) {
    pushAction(items, seen, fromLifecycleStep(step));
  }

  mergePackActions(input, items, seen);
  mergeWorkflowActions(input, items, seen);
  mergeOpenFollowOnAssessmentActions(input, items, seen);
  mergeDeliveryCheckBlockers(input, items, seen);
  mergePanelQueueExtras(input, items, seen);

  const deduped = dedupeSupersededLifecycleActions(items);

  const filtered = input.navigation?.suppressCarePlanReviewQueueItem
    ? deduped.filter((item) => item.id !== 'care-plan-review')
    : deduped;

  return filtered.sort((left, right) => {
    const severityDelta = SEVERITY_RANK[left.severity] - SEVERITY_RANK[right.severity];
    if (severityDelta !== 0) return severityDelta;
    return left.title.localeCompare(right.title);
  });
}

export function topUnifiedSahActions(
  queue: readonly UnifiedSahAction[],
  limit = 3,
): UnifiedSahAction[] {
  return queue.slice(0, limit);
}

export { isAgreementReadinessBlockerKey, CREATE_SERVICE_AGREEMENT_ACTION };
